/* eslint-disable import/prefer-default-export */
import { useCallback, useMemo, useState } from "react";

// eslint-disable-next-line import/extensions
import { RoiResultsData } from "../models/RoiResults";
// eslint-disable-next-line import/extensions
import { mapRoiCalculationToResults } from "../helpers/roiResultMapper";
// eslint-disable-next-line import/extensions
import { useRoiCalculationData } from "./useRoiCalculationData";
// eslint-disable-next-line import/extensions
import { useRoiCalculationFetch } from "./useRoiCalculationFetch";

const DEFAULT_CURRENCY = "EUR";

interface UseGetRoiResultsArgs {
  customerId?: string;
  farmId?: string;

  interventionReference?: string;
}

interface UseGetRoiResultsResult {
  roiResults: RoiResultsData | null;
  baselineName: string;
  interventionName: string;
  loading: boolean;
  /** Either request failed — the reason is logged, not surfaced as copy here. */
  failed: boolean;
  refetch: () => void;
}

export const useGetRoiResults = ({
  customerId,
  farmId,
  interventionReference,
}: UseGetRoiResultsArgs): UseGetRoiResultsResult => {
  const {
    data,
    loading: dataLoading,
    failed: dataFailed,
    refetch: refetchData,
  } = useRoiCalculationData(farmId, interventionReference);

  const [reloadKey, setReloadKey] = useState(0);

  const baselineReference = data?.baseline.reference ?? null;
  const hasIdentifiers = Boolean(
    customerId && baselineReference && interventionReference,
  );

  const refetch = useCallback(() => {
    refetchData();
    setReloadKey((current) => current + 1);
  }, [refetchData]);

  const snapshot = useRoiCalculationFetch({
    customerId,
    baselineReference,
    interventionReference,
    reloadToken: reloadKey,
  });

  const baselineName = data?.baseline.name ?? "";
  const interventionName = data?.intervention.name ?? "";

  const calculation =
    snapshot.status === "loaded" ? snapshot.calculation : null;

  const roiResults = useMemo(() => {
    if (!calculation) return null;
    return mapRoiCalculationToResults({
      calculation,
      parsedData: data,
      baselineName,
      interventionName,
      fallbackCurrency: data?.defaultCurrency ?? DEFAULT_CURRENCY,
    });
  }, [calculation, data, baselineName, interventionName]);

  return {
    roiResults,
    baselineName,
    interventionName,

    loading: dataLoading || (hasIdentifiers && snapshot.status === "loading"),
    failed: dataFailed || snapshot.status === "failed",
    refetch,
  };
};
